// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/channel/components/ChannelUserTag.module.css';
import {TeamsLogo} from '@app/features/ui/components/icons/TeamsLogo';
import {SparkleIcon} from '@app/features/ui/icons/Icons';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import React from 'react';

const ASSISTANT_DESCRIPTOR = msg({
	message: 'Assistant',
	comment:
		'Accessible name of the sparkle mark next to the Konvo Assistant (the system account) in messages and profiles.',
});

interface UserTagProps extends React.ComponentPropsWithoutRef<'span'> {
	className?: string;
	system?: boolean;
	/** KONVO: the account is a Teams contact, not a bot. */
	teams?: boolean;
	size?: 'sm' | 'lg';
}

export const UserTag = React.forwardRef<HTMLSpanElement, UserTagProps>(
	({className, system, teams, size = 'sm', ...props}, ref) => {
		const {i18n} = useLingui();
		// KONVO: the system account is the Konvo Assistant: a sparkle, not a "System" pill.
		if (system && !teams) {
			return (
				<span
					className={clsx(styles.assistant, className)}
					ref={ref}
					role="img"
					aria-label={i18n._(ASSISTANT_DESCRIPTOR)}
					data-flx="channel.user-tag.assistant"
					{...props}
				>
					<SparkleIcon size={size === 'lg' ? 16 : 14} weight="fill" />
				</span>
			);
		}
		return (
			<span
				className={clsx(styles.tag, size === 'lg' ? styles.tagLg : styles.tagSm, className)}
				ref={ref}
				data-flx="channel.user-tag.tag"
				{...props}
			>
				<span
					className={clsx(styles.text, size === 'lg' ? styles.textLg : styles.textSm)}
					data-flx="channel.user-tag.text"
				>
					{teams ? (
						<>
							<TeamsLogo size={size === 'lg' ? 12 : 10} />
							Teams
						</>
					) : (
						<Trans>Bot</Trans>
					)}
				</span>
			</span>
		);
	},
);

UserTag.displayName = 'UserTag';
